import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { PackageService } from '../../../../../services/package.service';
import { Package } from '../../../../../shared/types';
import { TemplateService } from '../../../../../car-wash-website/services/template.service';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;

export interface ExpressDetailsPanelData {
  title?: string;
  subtitle?: string;
  titleStyle?: TextEditorStyle;
  theme?: 'light' | 'dark';
  backgroundColor?: string;
  packages: ExpressDetailsPackageItem[];
}

export interface ExpressDetailsPackageItem {
  packageId: string;
  packageName: string;
  imageUrl: string;
  imageFit: 'fill' | 'cover' | 'contain' | 'fit';
  buttonText: string;
}

/**
 * ExpressDetailsPanelComponent - Panel for configuring an "Express Details" section used in a website template builder.
 * @component
 * @selector app-express-details-panel
 * @example
 *   <app-express-details-panel [data]="panelData" (close)="onClose()" (save)="onSave($event)" (dataChange)="onDataChanged($event)"></app-express-details-panel>
 *
 * Purpose:
 *   Presents UI for editing section-level content (title, subtitle), visual layout (theme, background color),
 *   and a list of service/detailing packages (images, image fit, button text). It synchronizes packages from an API,
 *   merges saved settings, supports image uploads, and emits updated data back to the parent.
 *
 * Inputs:
 * @input {ExpressDetailsPanelData} data - The panel model containing title, subtitle, theme, backgroundColor and packages.
 *
 * Outputs:
 * @output {EventEmitter<void>} close - Emitted when the user requests to close the panel without saving.
 * @output {EventEmitter<ExpressDetailsPanelData>} save - Emitted when the user saves the panel; sends final panel data.
 * @output {EventEmitter<ExpressDetailsPanelData>} dataChange - Emitted whenever panel data is changed (live updates).
 *
 * Key public properties:
 *   - activeTab: 'content' | 'layout' - Currently active editor tab.
 *   - expandedSections: Record<string, boolean> - Tracks expanded/collapsed state of editor sections.
 *   - packages: Package[] - Packages loaded from the API.
 *   - loading: boolean - Indicates whether package API request is in progress.
 *   - selectedTheme: 'light' | 'dark' - Local theme selection mirrored to data.theme.
 *   - customColors: string[] - One-off custom color(s) preserved when not in predefined colors.
 *
 * Important lifecycle hooks:
 *   - ngOnInit(): Loads packages from API, initializes selectedTheme from input data, and loads custom colors.
 *   - ngOnChanges(changes: SimpleChanges): Reacts to changes to the input data, and conditionally synchronizes
 *     packages with API data while avoiding overwriting user edits (uses packagesSynced guard).
 *
 * Key methods and behaviors:
 *   - loadPackages(): Fetches packages from PackageService, populates this.packages and triggers synchronization.
 *   - syncPackagesWithData(): Merges API packages with this.data.packages, maps image/scaletype values, and only
 *     emits a dataChange on initial sync to avoid overwriting user edits.
 *   - addMissingPackagesOnly(): Adds packages that exist in the API but are missing from this.data.packages without
 *     overwriting existing user-configured buttonText.
 *   - loadCustomColorFromData(): Sets customColors from data.backgroundColor when it is not a predefined color.
 *   - selectTheme(theme): Updates selectedTheme and data.theme and emits a dataChange.
 *   - onBackgroundColorChange(color): Updates data.backgroundColor and emits a dataChange.
 *   - openColorPicker(color?), closeColorPicker(), onColorPickerChange(): Manage the color picker UI, previewing
 *     and applying colors, and ensuring hex normalization.
 *   - addCustomColor(color): Adds or replaces the single allowed custom color if it is not in predefined colors.
 *   - getPackageData(packageId): Helper to find a package entry in data.packages.
 *   - onPackageImageChange / onPackageImageFitChange / onPackageButtonTextChange: Update specific package fields
 *     and emit dataChange.
 *   - onReplaceImage(packageId): Prompts the user to pick a file and validates size before upload.
 *   - handleFileUpload(packageId, file): Handles client-side preview, uploads via TemplateService, updates package
 *     records and API cache, and manages per-package uploading state and error messages.
 *   - emitDataChange(): Emits a shallow-cloned copy of this.data via dataChange for external synchronization.
 *   - onClose(): Emits the close output to request closing the panel.
 *   - onSave(): Emits the save output with a cloned copy of the current data payload.
 *
 * Notes / Implementation details:
 *   - Uses PackageService to fetch available packages and TemplateService for direct image uploads.
 *   - Protects user edits from being overwritten by saved API data by tracking packagesSynced and comparing
 *     servicesSectionPackages payloads on input changes.
 *   - Enforces image upload validation (max 5MB) and supports both temporary preview URLs and replacing with
 *     server-provided image URLs once upload completes.
 *   - Maintains upload progress/error state (uploadingImages, uploadErrors) to allow per-package UI feedback.
 */
@Component({
  selector: 'app-express-details-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, TextEditorComponent],
  templateUrl: './express-details-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './express-details-panel.component.scss',
})
export class ExpressDetailsPanelComponent implements OnInit, OnChanges {
  @Input() data: ExpressDetailsPanelData = {
    title: '',
    subtitle: '',
    theme: 'light',
    backgroundColor: '#FFFFFF',
    packages: [],
  };
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<ExpressDetailsPanelData>();
  @Output() dataChange = new EventEmitter<ExpressDetailsPanelData>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    theme: false,
    background: false,
  };
  packages: Package[] = [];
  loading = false;
  selectedTheme: 'light' | 'dark' = 'light';

  customColors: string[] = [];

  // Color picker state
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  // Image upload state
  uploadingImages: Record<string, boolean> = {};
  uploadErrors: Record<string, string | null> = {};

  // Track if packages have been synced initially to prevent overwriting user changes
  private packagesSynced = false;

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private templateService: TemplateService,
    private cdr: ChangeDetectorRef,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPackages();
    // Initialize theme from data
    if (this.data.theme) {
      this.selectedTheme = this.data.theme;
    }
    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  /**
   * Handle changes to the input data: sync packages on initial load or when servicesSectionPackages change, add missing packages on subsequent changes, and load a custom background color if provided.
   * @example
   * gOnChanges({ data: { servicesSectionPackages: [], backgroundColor: '#ffffff' } })
   * undefined
   * @param {SimpleChanges} changes - Angular SimpleChanges object containing the changed input values.
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data) {
        // Check if servicesSectionPackages has changed (new data loaded from API)
        const newServicesSectionPackages = (this.data as any).servicesSectionPackages || [];
        const previousServicesSectionPackages =
          (changes['data'].previousValue as any)?.servicesSectionPackages || [];

        // If servicesSectionPackages changed, it means new data was loaded, so we should sync
        const servicesSectionPackagesChanged =
          JSON.stringify(newServicesSectionPackages) !==
          JSON.stringify(previousServicesSectionPackages);

        // Only sync packages on initial load or when servicesSectionPackages changed
        // This prevents overwriting user changes with old saved data, but allows loading new saved data
        const shouldSync =
          !this.packagesSynced || changes['data'].firstChange || servicesSectionPackagesChanged;

        if (shouldSync) {
          this.syncPackagesWithData();
          this.packagesSynced = true;
        } else {
          // On subsequent changes, only add missing packages, don't overwrite existing buttonText
          this.addMissingPackagesOnly();
        }
      }
      if (this.data?.backgroundColor) {
        // Load custom color if backgroundColor is not in predefined colors
        this.loadCustomColorFromData();
      }
    }
  }

  /**
   * Load detailing packages from the package service, update component state, and synchronize or merge them with existing data.
   * @example
   * loadPackages()
   * // void
   * @returns {void} Does not return a value; updates this.packages, manages this.loading, and performs synchronization or merging as a side effect.
   */
  private loadPackages(): void {
    this.loading = true;
    this.packageService.getDetailingPackages().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.packages = response.data;
          // Only sync if packages haven't been synced yet (to avoid overwriting user changes)
          // The sync will happen in ngOnChanges when data is set
          if (!this.packagesSynced) {
            this.syncPackagesWithData();
          } else {
            // Just add missing packages without overwriting existing buttonText
            this.addMissingPackagesOnly();
          }
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('Error loading detailing packages:', err);
        this.loading = false;
      },
    });
  }

  /**
   * Synchronize local data.packages with the API packages: add missing packages, update names, images, imageFit and buttonText, and respect saved settings; emit a data-change only on the initial sync.
   * @example
   * this.syncPackagesWithData()
   * undefined
   * @returns {{void}} No return value.
   **/
  private syncPackagesWithData(): void {
    if (!this.packages.length) return;

    const existingPackageIds = new Set(this.data.packages.map(p => p.packageId));

    // Add missing packages from API
    this.packages.forEach(pkg => {
      if (!existingPackageIds.has(pkg.id)) {
        // Use API image if available, otherwise empty string (will show placeholder)
        const apiImage = pkg.image && pkg.image.trim() !== '' ? pkg.image : '';
        // Check if package has imageFit/scaletype in API data
        const apiImageFit = (pkg as any).imageFit || (pkg as any).scaletype || 'fill';
        // Map API scaletype to panel imageFit (fill, cover, contain, fit)
        let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
        if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
          imageFit = apiImageFit;
        } else if (apiImageFit === 'stretch') {
          imageFit = 'fill';
        }
        this.data.packages.push({
          packageId: pkg.id,
          packageName: pkg.package_name || '',
          imageUrl: apiImage, // Use API image if available
          imageFit: imageFit,
          buttonText: (pkg as any).buttonText || 'Buy Now',
        });
      }
    });

    // Update package names, images, and imageFit if they changed in API
    // First, try to get settings from services_section.packages (saved settings)
    const servicesSectionPackages = (this.data as any).servicesSectionPackages || [];

    this.data.packages.forEach(pkgData => {
      const apiPackage = this.packages.find(p => p.id === pkgData.packageId);
      if (apiPackage) {
        pkgData.packageName = apiPackage.package_name || pkgData.packageName;
        // Update imageUrl from API if it's currently empty and API has an image
        if (!pkgData.imageUrl && apiPackage.image && apiPackage.image.trim() !== '') {
          pkgData.imageUrl = apiPackage.image;
        }
      }

      // Check services_section.packages for saved imageFit and buttonText
      const savedPackageConfig = servicesSectionPackages.find(
        (p: any) => p.id === pkgData.packageId
      );
      if (savedPackageConfig) {
        // Update imageFit from saved settings
        if (savedPackageConfig.scaletype) {
          let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
          const scaletype = savedPackageConfig.scaletype;
          if (scaletype === 'cover' || scaletype === 'contain' || scaletype === 'fit') {
            imageFit = scaletype;
          } else if (scaletype === 'stretch') {
            imageFit = 'fill';
          }
          pkgData.imageFit = imageFit;
        }
        // Update buttonText from saved settings
        if (savedPackageConfig.buttonText) {
          pkgData.buttonText = savedPackageConfig.buttonText;
        }
      } else {
        // Fallback: check API package directly
        if (apiPackage) {
          const apiImageFit = (apiPackage as any).imageFit || (apiPackage as any).scaletype;
          if (apiImageFit) {
            let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
            if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
              imageFit = apiImageFit;
            } else if (apiImageFit === 'stretch') {
              imageFit = 'fill';
            }
            pkgData.imageFit = imageFit;
          }
          // Update buttonText from API if available
          if ((apiPackage as any).buttonText) {
            pkgData.buttonText = (apiPackage as any).buttonText;
          }
        }
      }
    });

    // Only emit data change on initial sync to avoid overwriting user changes
    if (!this.packagesSynced) {
      this.emitDataChange();
    }
  }

  // Add missing packages only, without overwriting existing buttonText
  /**
   * Add any packages from this.packages into this.data.packages if they do not already exist, normalizing image URL and imageFit and preserving any saved per-package settings (such as scaletype and buttonText).
   * @example
   * this.addMissingPackagesOnly()
   * // undefined (mutates this.data.packages by appending missing package entries)
   * @returns {void} Does not return a value; updates this.data.packages in place.
   */
  private addMissingPackagesOnly(): void {
    if (!this.packages.length) return;

    const existingPackageIds = new Set(this.data.packages.map(p => p.packageId));

    // Add missing packages from API
    this.packages.forEach(pkg => {
      if (!existingPackageIds.has(pkg.id)) {
        // Use API image if available, otherwise empty string (will show placeholder)
        const apiImage = pkg.image && pkg.image.trim() !== '' ? pkg.image : '';
        // Check if package has imageFit/scaletype in API data
        const apiImageFit = (pkg as any).imageFit || (pkg as any).scaletype || 'fill';
        // Map API scaletype to panel imageFit (fill, cover, contain, fit)
        let imageFit: 'fill' | 'cover' | 'contain' | 'fit' = 'fill';
        if (apiImageFit === 'cover' || apiImageFit === 'contain' || apiImageFit === 'fit') {
          imageFit = apiImageFit;
        } else if (apiImageFit === 'stretch') {
          imageFit = 'fill';
        }

        // Check saved settings for this new package
        const servicesSectionPackages = (this.data as any).servicesSectionPackages || [];
        const savedPackageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

        this.data.packages.push({
          packageId: pkg.id,
          packageName: pkg.package_name || '',
          imageUrl: apiImage,
          imageFit: savedPackageConfig?.scaletype
            ? savedPackageConfig.scaletype === 'cover' ||
              savedPackageConfig.scaletype === 'contain' ||
              savedPackageConfig.scaletype === 'fit'
              ? savedPackageConfig.scaletype
              : 'fill'
            : imageFit,
          buttonText:
            savedPackageConfig?.buttonText || (pkg as any).buttonText || 'Book Appointment',
        });
      }
    });
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Load a custom background color from component data into customColors if it's not a predefined color.
   * @example
   * loadCustomColorFromData()
   * // If this.data.backgroundColor exists and is not in PREDEFINED_COLORS, this.customColors becomes [backgroundColor]; otherwise it's cleared to [].
   * @returns {void} No return value.
   **/
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!ExpressDetailsPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  onClose(): void {
    this.close.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle expansion for a named panel section; collapsing other standard sections when expanding one.
   * @example
   * toggleSection('heading')
   * undefined
   * @param {string} section - Section identifier to toggle (e.g. 'heading', 'theme', 'background').
   * @returns {void} No return value.
   **/
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      // Only do this for standard sections (heading, theme, background)
      if (section === 'heading' || section === 'theme' || section === 'background') {
        Object.keys(this.expandedSections).forEach(key => {
          if (key === 'heading' || key === 'theme' || key === 'background') {
            this.expandedSections[key] = false;
          }
        });
      }
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onTitleChange(value: string): void {
    this.data.title = value;
    this.emitDataChange();
  }

  onTitleStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitDataChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitDataChange();
  }

  selectTheme(theme: 'light' | 'dark'): void {
    this.selectedTheme = theme;
    this.data.theme = theme;
    this.emitDataChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitDataChange();
  }

  onAddColor(): void {
    // Open color picker modal
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Handle loss of focus/closure of the color picker: apply the current picker color (ensuring a leading '#'), add it to custom colors (replacing the single allowed custom color), and close the picker.
   * @example
   * loseColorPicker()
   * undefined
   * @returns {{void}} No return value.
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize the selected color from the color picker to a hex string, store it on the component, and apply it as a preview.
   * @example
   * nColorPickerChange()
   * undefined
   * @param {void} none - This method does not accept any arguments.
   * @returns {void} Updates this.currentPickerColor to a normalized hex string (ensures leading '#') and calls this.onBackgroundColorChange(hex) to apply a preview without persisting to custom colors.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!ExpressDetailsPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  getPackageData(packageId: string): ExpressDetailsPackageItem | undefined {
    return this.data.packages.find(p => p.packageId === packageId);
  }

  onPackageImageChange(packageId: string, imageUrl: string): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.imageUrl = imageUrl;
      this.emitDataChange();
    }
  }

  onPackageImageFitChange(packageId: string, imageFit: 'fill' | 'cover' | 'contain' | 'fit'): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.imageFit = imageFit;
      this.emitDataChange();
    }
  }

  onPackageButtonTextChange(packageId: string, buttonText: string): void {
    const pkgData = this.getPackageData(packageId);
    if (pkgData) {
      pkgData.buttonText = buttonText;
      this.emitDataChange();
    }
  }

  /**
   * Open a file picker to select an image or MP4, validate it (max 5MB), and upload it for the given package.
   * @example
   * nReplaceImage('package123')
   * undefined
   * @param {{string}} {{packageId}} - The identifier of the package to associate the selected file with.
   * @returns {{void}} Does not return a value; triggers file selection and upload flow.
   **/
  onReplaceImage(packageId: string): void {
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg,image/gif,video/mp4';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.messageService.add({
            severity: 'warn',
            summary: 'File Too Large',
            detail: 'File size must not exceed 5MB.',
          });
          return;
        }

        // Upload file to server
        this.handleFileUpload(packageId, file);
      }
    };
    input.click();
  }

  /**
   * Handle uploading an image file for a package: create a temporary preview, upload to the package endpoint, update package data and UI, and manage loading/error state.
   * @example
   * handleFileUpload('package-id', file)
   * undefined
   * @param {{string}} {{packageId}} - ID of the package to which the file belongs.
   * @param {{File}} {{file}} - Image file to upload and preview.
   * @returns {{void}} No return value; updates component state and UI (uploads image, replaces preview with uploaded URL, updates package array, and triggers change detection).
   **/
  private handleFileUpload(packageId: string, file: File): void {
    // Show loading state
    this.uploadingImages[packageId] = true;
    this.uploadErrors[packageId] = null;

    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    this.onPackageImageChange(packageId, tempPreviewUrl);

    // Upload directly to package endpoint
    this.templateService.uploadPackageImage(packageId, file).subscribe({
      next: response => {
        // Extract image URL from response
        let imageUrl = '';
        if (response?.data?.imageUrl) {
          imageUrl = response.data.imageUrl;
        } else if (response?.imageUrl) {
          imageUrl = response.imageUrl;
        } else if (typeof response === 'string') {
          imageUrl = response;
        }

        if (imageUrl) {
          // Replace temporary URL with uploaded URL
          URL.revokeObjectURL(tempPreviewUrl);

          // Update the package data immediately
          this.onPackageImageChange(packageId, imageUrl);

          // Also update the API package array so it reflects the new image
          // This ensures the image shows immediately without needing to reload
          const apiPackage = this.packages.find(p => p.id === packageId);
          if (apiPackage) {
            apiPackage.image = imageUrl;
          }

          // Force change detection to update the UI immediately
          this.cdr.detectChanges();
        } else {
          this.logger.error('No image URL in response:', response);
          this.uploadErrors[packageId] = 'Failed to get image URL from server';
        }

        this.uploadingImages[packageId] = false;
      },
      error: error => {
        this.logger.error('Error uploading package image:', error);
        this.uploadErrors[packageId] = 'Failed to upload image. Please try again.';
        this.uploadingImages[packageId] = false;
        // Keep the temporary preview on error
      },
    });
  }

  private emitDataChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  onSave(): void {
    this.save.emit({ ...this.data });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
